/* eslint-disable import/no-cycle */
import {
  ICase,
  INameValidation,
  ICreateFolder,
  IData,
  IFolder,
  ILabel,
  IList,
  IMixedData,
  IResponse,
  ISaveCasePayload,
  IShareAccess,
  IShared,
  IMixedSelectedItem,
  IImageAddLabels,
  IShareRemove,
  IShareBatchRemove,
  IMedia, ICaseFileOrder,
} from '@/types/interfaces';
import ApiService from '@/services/ApiService';
import progressConfig from '@/helpers/progressConfig';
import { SHARE_TYPES, SORT_TYPE } from '@/types/enums';
import { ITEMS_ON_PAGE } from '@/types/consts';

export default class DriveService {
  public static getCases(page: number, searchTerm: string): Promise<IResponse<IData<IList<ICase>>>> {
    const payload = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'UPDATED', order: SORT_TYPE.DESC }],
      searchTerm,
    };
    return ApiService.postAndReturnData('/cases', payload);
  }

  public static getDesktopCases(page: number, searchTerm: string): Promise<IResponse<IData<IList<ICase>>>> {
    const payload = {
      paging: { page, itemsOnPage: 100 },
      sort: [{ field: 'UPDATED', order: SORT_TYPE.DESC }],
      searchTerm,
    };
    return ApiService.postAndReturnData('/cases', payload);
  }

  public static getPublicCases(page: number, searchTerm: string): Promise<IResponse<IData<IList<ICase>>>> {
    const payload = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'UPDATED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'PUBLIC', value: true }],
    };
    return ApiService.postAndReturnData('/cases', payload);
  }

  public static getUserPublicCases(page: number, searchTerm: string, accountId: string): Promise<IResponse<IData<IList<ICase>>>> {
    const payload = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'UPDATED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'ACCOUNT', value: accountId }, { field: 'PUBLIC', value: true }],
    };
    return ApiService.postAndReturnData('/cases', payload);
  }

  public static getNetworkCases(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [
        {
          field: 'SHARED',
          order: SORT_TYPE.DESC,
        }],
      searchTerm,
      filters: [{ field: 'TYPE', value: 'CASE' }],
    };
    if (folderId) {
      payload.filters = [...payload.filters, { field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/shares', payload);
  }

  public static getAllCases(page: number, searchTerm: string): Promise<IResponse<IData<IList<ICase>>>> {
    const payload = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'UPDATED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'WITH_SHARED', value: true }],
    };
    return ApiService.postAndReturnData('/cases', payload);
  }

  public static getNetworkPhotos(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'SHARED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'TYPE', value: 'FILE' }, { field: 'EXACT_TYPE', value: 'FILE_PHOTO' }, { field: 'FILE_DISTINCT_BY_CONTENT', value: true }],
    };
    if (folderId) {
      payload.filters = [...payload.filters, { field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/shares', payload);
  }

  public static getDesktopNetworkPhotos(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: 100 },
      sort: [{ field: 'SHARED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'TYPE', value: 'FILE' }, { field: 'EXACT_TYPE', value: 'FILE_PHOTO' }, { field: 'FILE_DISTINCT_BY_CONTENT', value: true }],
    };
    if (folderId) {
      payload.filters = [...payload.filters, { field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/shares', payload);
  }

  public static getNetworkFiles(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'SHARED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'TYPE', value: 'FILE' }, { field: 'EXACT_TYPE', value: 'FILE_FILE' }],
    };
    if (folderId) {
      payload.filters = [...payload.filters, { field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/shares', payload);
  }

  public static getMixedData(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{
        field: 'FOLDER_SORT',
        order: 'DESC',
      },
      {
        field: 'UPDATED',
        order: 'DESC',
      }],
      searchTerm,
    };
    if (folderId) {
      payload.filters = [{ field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/mixed', payload);
  }

  public static getDesktopMixedData(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: 100 },
      sort: [{
        field: 'FOLDER_SORT',
        order: 'DESC',
      },
      {
        field: 'UPDATED',
        order: 'DESC',
      }],
      searchTerm,
    };
    if (folderId) {
      payload.filters = [{ field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/mixed', payload);
  }

  public static getNetworkMixedData(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: ITEMS_ON_PAGE },
      sort: [{ field: 'SHARED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'DIRECTLY_SHARED', value: true }],
    };
    if (folderId) {
      payload.filters = [{ field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/shares', payload);
  }

  public static getDesktopNetworkMixedData(page: number, searchTerm: string, folderId?: string): Promise<IResponse<IData<IList<IMixedData>>>> {
    const payload: any = {
      paging: { page, itemsOnPage: 100 },
      sort: [{ field: 'SHARED', order: SORT_TYPE.DESC }],
      searchTerm,
      filters: [{ field: 'DIRECTLY_SHARED', value: true }],
    };
    if (folderId) {
      payload.filters = [{ field: 'UPPER_FOLDER', value: folderId }];
    }
    return ApiService.postAndReturnData('/shares', payload);
  }

  public static getFolderById(folderId: string): Promise<IResponse<IData<IFolder>>> {
    return ApiService.getAndReturnData(`/folder/${folderId}`);
  }

  public static createFolder(payload: ICreateFolder): Promise<IResponse<IData<IFolder>>> {
    return ApiService.postAndReturnData('/folder/save', payload);
  }

  public static renameMedia(payload: any): Promise<IResponse> {
    return ApiService.postAndReturnData('/files/update', payload);
  }

  public static deleteFolder(id: string): Promise<IResponse> {
    return ApiService.postAndReturnData('/folder/delete/', { id });
  }

  public static getWhereFileUsing(fileId: string): Promise<IResponse<IData<ICase[]>>> {
    return ApiService.getAndReturnData(`cases/with-file/${fileId}`);
  }

  public static addFileLabels(fileId: string, payload: ILabel[]): Promise<IResponse<any>> {
    return ApiService.postAndReturnData(`/files/label/assign/${fileId}`, payload);
  }

  public static addImageLabels(payload: IImageAddLabels): Promise<IResponse<IData<IMedia>>> {
    return ApiService.postAndReturnData('/files/update', payload);
  }

  public static createCase(payload: ISaveCasePayload, onUpdate?: (percent: number) => void): Promise<IResponse<IData<ICase>>> {
    const config = progressConfig(onUpdate);
    return ApiService.postAndReturnData('/cases/save', payload, config);
  }

  public static deleteCase(id: string | null): Promise<IResponse> {
    if (!id) {
      return Promise.reject();
    }
    return ApiService.postAndReturnData('/cases/delete', { id });
  }

  public static saveCaseAsCopy(id: string | null): Promise<IResponse<IData<ICase>>> {
    if (!id) {
      return Promise.reject();
    }
    return ApiService.postAndReturnData(`/cases/copy/${id}`);
  }

  public static saveMediaAsCopy(id: string): Promise<IResponse<IData<IMedia>>> {
    return ApiService.postAndReturnData(`/files/copy/${id}`);
  }

  public static saveFolderAsCopy(id: string): Promise<IResponse> {
    return ApiService.postAndReturnData(`/folder/copy/${id}`);
  }

  public static updateCase(payload: ISaveCasePayload, onUpdate?: (percent: number) => void): Promise<IResponse<IData<ICase>>> {
    const config = progressConfig(onUpdate);
    return ApiService.postAndReturnData('/cases/save', payload, config);
  }

  public static getCaseById(id: string): Promise<IResponse<IData<ICase>>> {
    return ApiService.getAndReturnData(`/cases/${id}`);
  }

  public static makeCasePublic(id: string): Promise<IResponse<IData<ICase>>> {
    return ApiService.postAndReturnData(`/cases/public/${id}`);
  }

  public static unmakeCasePublic(id: string): Promise<IResponse<IData<ICase>>> {
    return ApiService.deleteAndReturnData(`/cases/public/${id}`);
  }

  public static getCaseExternalUrl(id: string): Promise<IResponse<IData<ICase>>> {
    return ApiService.postAndReturnData(`/cases/trigger-external-url/${id}`);
  }

  public static handleCaseExternalUrl(payload: { caseId: string; flag: boolean }): Promise<IResponse<IData>> {
    return ApiService.postAndReturnData('/cases/toggle-external-url', payload);
  }

  public static fetchSharedCaseInfo(id: string): Promise<IResponse<IData<IShared | null>>> {
    return ApiService.getAndReturnData(`/cases/shared/${id}`);
  }

  public static fetchSharedMediaInfo(id: string): Promise<IResponse<IData<IShared | null>>> {
    return ApiService.getAndReturnData(`/files/shared/${id}`); // TODO MEdiaService
  }

  public static fetchSharedFolderInfo(id: string): Promise<IResponse<IData<IShared | null>>> {
    return ApiService.getAndReturnData(`/folder/shared/${id}`);
  }

  // public static async saveAndReturnFile(payload: FormData): Promise<IResponse<IData<IMedia>>> {
  //   return ApiService.postAndReturnData('files/upload', payload);
  // }

  public static async shareAccess(id: string, payload: IData<IShareAccess[]>, shareType: SHARE_TYPES): Promise<IResponse> {
    if (shareType === SHARE_TYPES.CASE) {
      return ApiService.postAndReturnData(`cases/share/${id}`, payload);
    }
    if (shareType === SHARE_TYPES.FOLDER) {
      return ApiService.postAndReturnData(`folder/share/${id}`, payload);
    }
    return ApiService.postAndReturnData(`files/share/${id}`, payload);
  }

  public static async removeShare(payload: IShareRemove): Promise<IResponse> {
    return ApiService.deleteAndReturnData('/shares/remove', payload);
  }

  public static async removeBatchShare(payload: IShareBatchRemove): Promise<IResponse> {
    return ApiService.deleteAndReturnData('/shares/remove/bunch', payload);
  }

  public static async removeFromSharing(payload: IShareBatchRemove): Promise<IResponse> {
    return ApiService.deleteAndReturnData('/shares/remove/me/bunch', payload);
  }

  public static async moveCase(id: string, folderId: string | null): Promise<IResponse> {
    const payload = {
      id,
      folderId,
    };
    return ApiService.postAndReturnData('/cases/move-to', payload);
  }

  public static async moveFolder(id: string, folderId: string | null): Promise<IResponse> {
    const payload = {
      id,
      folderId,
    };
    return ApiService.postAndReturnData('/folder/move-to', payload);
  }

  public static async moveFile(id: string, folderId: string | null): Promise<IResponse> {
    const payload = {
      id,
      folderId,
      saveMode: 'RENAME',
    };
    return ApiService.postAndReturnData('/files/move-to', payload);
  }

  public static async deleteBatchCases(ids: string[]): Promise<IResponse> {
    return ApiService.postAndReturnData('/cases/delete/bunch', { ids });
  }

  public static async deleteBatchFiles(ids: string[]): Promise<IResponse> {
    return ApiService.postAndReturnData('/files/delete/bunch', { ids });
  }

  public static async deleteBatchMixed(ids: IMixedSelectedItem[]): Promise<IResponse> {
    return ApiService.postAndReturnData('/mixed/delete', { ids });
  }

  public static async setFilesOrder(payload: ICaseFileOrder): Promise<IResponse> {
    return ApiService.postAndReturnData('/files/order', JSON.stringify(payload));
  }
}
